import { type FileService } from "@/shared/domain/files";
import {
  type MessagePathRepresentationMapping,
  type RepresentationSelector,
  type TopicService,
  RepresentationStorageFormat,
  resolveMessagePathFiles,
} from "@/shared/domain/topics";

import { McapTopicReader } from "./McapTopicReader";
import { ParquetMetadataReader } from "./parquet/ParquetMetadataReader";
import { ParquetTopicReader } from "./ParquetTopicReader";
import { type TopicReader } from "./TopicReader";

/**
 * Stable serialization of a {@link RepresentationSelector} for use as part of a
 * cache key. Returns `undefined` when the selector imposes no constraint
 * (either omitted or with both fields unset), so the no-selector case shares a
 * key with semantically-equivalent empty selectors.
 *
 * `contentFormat` matching is case-insensitive and order-insensitive when a
 * list, so we lowercase and sort. `transformations` matching is order-sensitive
 * (see {@link selectorMatches}), so we preserve order.
 */
function serializeSelector(
  selector: RepresentationSelector | undefined,
): string | undefined {
  if (selector === undefined) {
    return undefined;
  }
  const { contentFormat, transformations } = selector;
  if (contentFormat === undefined && transformations === undefined) {
    return undefined;
  }
  const normalizedContentFormat =
    contentFormat === undefined
      ? undefined
      : (Array.isArray(contentFormat) ? [...contentFormat] : [contentFormat])
          .map((f) => f.toLowerCase())
          .sort();
  return JSON.stringify({
    contentFormat: normalizedContentFormat,
    transformations,
  });
}

/**
 * Usage:
 *
 *  const topic = panelState[0]; // TopicData
 *  const registry = new ReaderRegistry({ ... });
 *  const reader = await registry.forTopic(topic.id);
 *  for await(const message of reader.iterMessages()) { ... };
 */
export class ReaderRegistry {
  private fileService: FileService;
  private topicService: TopicService;
  private topicToReader = new Map<string, TopicReader>();
  // Worker-scoped dedup of representation fetches across calls for different
  // message paths in the same topic. Populated on first successful fetch.
  // Topic representations are append-only and worker lifetimes are short, so
  // no invalidation is needed.
  private topicToMappings = new Map<
    string,
    MessagePathRepresentationMapping[]
  >();
  private parquetMetadataReader: ParquetMetadataReader;

  constructor({
    fileService,
    topicService,
  }: {
    fileService: FileService;
    topicService: TopicService;
  }) {
    this.fileService = fileService;
    this.topicService = topicService;
    this.parquetMetadataReader = new ParquetMetadataReader();
  }

  dispose(
    topicId: string,
    messagePathId?: string,
    representationSelector?: RepresentationSelector,
  ): void {
    const key = this.keyFor(topicId, messagePathId, representationSelector);
    try {
      this.topicToReader.get(key)?.dispose();
    } finally {
      this.topicToReader.delete(key);
    }
  }

  async forTopic(params: {
    topicId: string;
    messagePathId?: string;
    representationSelector?: RepresentationSelector;
    signal?: AbortSignal;
  }): Promise<TopicReader> {
    const { topicId, messagePathId, representationSelector, signal } = params;
    const key = this.keyFor(topicId, messagePathId, representationSelector);
    if (this.topicToReader.has(key)) {
      return this.topicToReader.get(key)!;
    }

    await navigator.locks.request(
      `ReaderRegistry::forTopic#${key}`,
      { signal },
      async () => {
        signal?.throwIfAborted();

        if (this.topicToReader.has(key)) {
          // Another ~concurrent access attempt might have beaten this to the lock acquisition.
          return;
        }

        const reader = await this.createReader(
          topicId,
          representationSelector,
          signal,
        );
        this.topicToReader.set(key, reader);
      },
    );
    signal?.throwIfAborted();

    const reader = this.topicToReader.get(key);
    if (reader === undefined) {
      throw new Error(
        `Failed to create a reader for data from topic ${topicId}. Please reach out to Roboto support.`,
      );
    }
    return reader;
  }

  private keyFor(
    topicId: string,
    messagePathId?: string,
    selector?: RepresentationSelector,
  ): string {
    const base =
      messagePathId === undefined ? topicId : `${topicId}#${messagePathId}`;
    const selectorKey = serializeSelector(selector);
    return selectorKey === undefined ? base : `${base}::${selectorKey}`;
  }

  private async getMappings(
    topicId: string,
    signal?: AbortSignal,
  ): Promise<MessagePathRepresentationMapping[]> {
    const cached = this.topicToMappings.get(topicId);
    if (cached) {
      return cached;
    }
    const mappings = await this.topicService.getMessagePathRepresentations(
      topicId,
      { abortSignal: signal },
    );
    this.topicToMappings.set(topicId, mappings);
    return mappings;
  }

  private async createReader(
    topicId: string,
    selector: RepresentationSelector | undefined,
    signal?: AbortSignal,
  ): Promise<TopicReader> {
    const allMappings = await this.getMappings(topicId, signal);
    const resolved = resolveMessagePathFiles(
      allMappings.filter((m) => m.representation !== undefined),
      selector,
    );
    if (resolved.length === 0) {
      const cause = selector
        ? `No representation matches the requested selector for topic ${topicId}`
        : `No representations found for topic ${topicId}`;
      throw new Error(
        "Encountered an error reading topic data. Please reach out to Roboto support.",
        { cause },
      );
    }

    // All resolved mappings share the same storage format (selectRepresentation
    // is applied per message path, but the API groups by format at the topic level).
    const fmt = resolved[0].representation.storage_format;
    switch (fmt) {
      case RepresentationStorageFormat.MCAP: {
        return new McapTopicReader(topicId, resolved, this.fileService);
      }
      case RepresentationStorageFormat.PARQUET: {
        return new ParquetTopicReader(
          topicId,
          resolved,
          this.fileService,
          this.parquetMetadataReader,
        );
      }
      default: {
        throw new Error(
          `Support for reading data stored as ${String(fmt)} not yet implemented.`,
        );
      }
    }
  }
}
